import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Swal from "sweetalert2";
import useApi from "../hooks/useApi";

const loadRazorpayScript = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error("Unable to load Razorpay checkout."));
    document.body.appendChild(script);
  });

const RegisterInterestPayment = () => {
  const { token } = useParams();
  const { publicAuth } = useApi();
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [error, setError] = useState("");

  const fetchDetails = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await publicAuth(`register-interest-payment/${token}`, "GET");
      setDetails(res?.data?.data || null);
    } catch (err) {
      setError(err?.response?.data?.message || "Payment link is invalid or expired.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const startPayment = async () => {
    if (!details || details.payment_status === "paid") return;

    try {
      setPaying(true);
      await loadRazorpayScript();

      const orderRes = await publicAuth(`register-interest-payment/${token}/order`, "POST");
      const order = orderRes?.data?.data;
      if (!order?.key || !order?.order_id) {
        throw new Error("Payment order could not be created.");
      }

      const paymentResult = await new Promise((resolve, reject) => {
        const razorpay = new window.Razorpay({
          key: order.key,
          amount: order.amount_paisa,
          currency: order.currency || "INR",
          name: "Asysta",
          description: "Enrollment payment",
          order_id: order.order_id,
          prefill: {
            name: order.name || details.name || "",
            email: order.email || details.email || "",
            contact: order.phone || details.phone || "",
          },
          theme: { color: "#0f766e" },
          handler: (response) => resolve(response),
          modal: {
            ondismiss: () => reject(new Error("Payment was cancelled.")),
          },
        });

        razorpay.open();
      });

      await publicAuth(`register-interest-payment/${token}/verify`, "POST", {
        razorpay_order_id: paymentResult.razorpay_order_id,
        razorpay_payment_id: paymentResult.razorpay_payment_id,
        razorpay_signature: paymentResult.razorpay_signature,
      });

      await Swal.fire({
        icon: "success",
        title: "Payment successful",
        text: "Your enrollment is now active. You can login with your email and password sent on email.",
        confirmButtonColor: "#0f766e",
      });

      fetchDetails();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Payment failed",
        text: err?.response?.data?.message || err?.message || "Please try again.",
        confirmButtonColor: "#0f766e",
      });
    } finally {
      setPaying(false);
    }
  };

  const amount = details?.amount ? Number(details.amount).toFixed(2) : "0.00";
  const paid = details?.payment_status === "paid";

  return (
    <main style={{ background: "#f6f8fb", minHeight: "70vh", padding: "56px 16px" }}>
      <section className="container" style={{ maxWidth: 860 }}>
        <div
          style={{
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 10,
            boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
            overflow: "hidden",
          }}
        >
          <div style={{ background: "#0f766e", color: "#fff", padding: "28px 32px" }}>
            <h1 style={{ margin: 0, fontSize: 28, lineHeight: 1.25 }}>Complete Enrollment Payment</h1>
          </div>

          <div style={{ padding: "30px 32px" }}>
            {loading ? (
              <p style={{ margin: 0, color: "#64748b" }}>Loading payment details...</p>
            ) : error ? (
              <div className="alert alert-danger mb-0">{error}</div>
            ) : (
              <>
                <div className="row g-3 mb-4">
                  <div className="col-md-6">
                    <div className="text-muted small">Name</div>
                    <strong>{details?.name || "-"}</strong>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small">Course</div>
                    <strong>{details?.course || "Selected Course"}</strong>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small">City</div>
                    <strong>{details?.city || "-"}</strong>
                  </div>
                  <div className="col-md-6">
                    <div className="text-muted small">Driving School</div>
                    <strong>{details?.school || "-"}</strong>
                  </div>
                </div>

                <div
                  className="d-flex flex-wrap align-items-center justify-content-between gap-3"
                  style={{ borderTop: "1px solid #e5e7eb", paddingTop: 22 }}
                >
                  <div>
                    <div className="text-muted small">Payable Amount</div>
                    <div style={{ fontSize: 30, fontWeight: 800, color: "#111827" }}>
                      {details?.currency || "INR"} {amount}
                    </div>
                    <span className={`badge ${paid ? "bg-success" : "bg-warning text-dark"}`}>
                      {paid ? "Paid" : "Pending"}
                    </span>
                  </div>

                  <button
                    type="button"
                    className="btn btn-lg"
                    onClick={startPayment}
                    disabled={paying || paid}
                    style={{ background: paid ? "#16a34a" : "#0f766e", color: "#fff", minWidth: 180 }}
                  >
                    {paid ? "Payment Complete" : paying ? "Processing..." : "Pay Now"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </main>
  );
};

export default RegisterInterestPayment;
